import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import type { JobProfileSkill } from '~/shared/data-contract/skill';
import { DropdownMenuItem } from '~/ui/core/dropdown-menu/DropdownMenu';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { ProficiencyScale } from '~/ui/domain/skill/ProficiencyScale';

import { useShortSkillDescriptionsPreferred } from '../../../app/common/hooks/use-short-skill-descriptions-preferred';
import { getProficiencyScaleCount, useSkillProficiencyLevelName } from '../../../app/common/skills';
import { useSkillDetails } from '../../api/use-skill-details';

export const ProficiencyLevelDropdownMenu: FunctionComponent<{
    readonly id: string;
    readonly activeLevel: number | null | undefined;
    readonly disabled: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
}> = ({ id, activeLevel, disabled, onChangeProficiencyLevel }) => {
    const { getProficiencyLevelName } = useSkillProficiencyLevelName();
    const { data: skillDetails } = useSkillDetails(id);
    const isShortSkillDescriptionPreferred = useShortSkillDescriptionsPreferred();

    if (is.undefined(skillDetails)) {
        return (
            <ScrollArea
                className="!h-auto max-h-[calc(var(--radix-dropdown-menu-content-available-height)-0.5rem)]"
                orientation="vertical"
                type="hover"
                viewportClassName="!h-auto max-h-[calc(var(--radix-dropdown-menu-content-available-height)-0.5rem)]"
            >
                {Array.from({ length: 10 }, (_, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                    <DropdownMenuItem key={index}>
                        <span data-testid="loading-placeholder" />
                    </DropdownMenuItem>
                ))}
                <DropdownMenuItem />
            </ScrollArea>
        );
    }

    return (
        <ScrollArea
            className="!h-auto max-h-[calc(var(--radix-dropdown-menu-content-available-height)-0.5rem)]"
            orientation="vertical"
            type="hover"
            viewportClassName="!h-auto max-h-[calc(var(--radix-dropdown-menu-content-available-height)-0.5rem)]"
        >
            {skillDetails?.proficiencyLevelDetails?.map((level) => (
                <DropdownMenuItem
                    key={level.proficiencyLevel}
                    disabled={disabled}
                    onClick={() => onChangeProficiencyLevel(id, level.proficiencyLevel)}
                >
                    <div className="flex max-w-[27.75rem] flex-col justify-end gap-s text-text-secondary">
                        <h4 className="m-0 flex flex-row items-start justify-start gap-s text-body-s font-medium text-text-primary tracking-tight">
                            {getProficiencyLevelName({
                                level: level.proficiencyLevel,
                                skillType: skillDetails.skillType,
                            })}
                            <ProficiencyScale
                                active={activeLevel === level.proficiencyLevel}
                                level={level.proficiencyLevel}
                                scaleCount={getProficiencyScaleCount(skillDetails.skillType)}
                            />
                        </h4>
                        <div>
                            {isShortSkillDescriptionPreferred &&
                            is.nonEmptyStringAndNotWhitespace(level.shortDescription)
                                ? level.shortDescription
                                : (level.description ?? '')}
                        </div>
                    </div>
                </DropdownMenuItem>
            ))}
            <DropdownMenuItem />
        </ScrollArea>
    );
};
